import {useLayoutEffect, useRef, type CSSProperties} from 'react'
import {gsap} from 'gsap'
import {useTheme} from '@primer/react-brand/esm'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import styles from './MonaFooterGrid.module.css'
import type {MonaFooterProps} from './types'
import {useMonaFooterLayout} from './use-mona-footer-layout'

import tileGradient1 from './tile-gradient-1.webp'
import tileGradient2 from './tile-gradient-2.webp'

const BACKGROUNDS = ['#FF80D2', '#0FBF3E', '#C898FD'] as const
const HOVER_BACKGROUNDS = [...BACKGROUNDS, `url(${tileGradient1})`, `url(${tileGradient2})`]
const REPOSITORY_COLORS = [
  {
    background: 'var(--base-color-scale-purple-5)',
    lightName: 'var(--base-color-scale-purple-5)',
    darkName: 'var(--base-color-scale-purple-2)',
  },
  {
    background: 'var(--base-color-scale-green-4)',
    lightName: 'var(--base-color-scale-green-6)',
    darkName: 'var(--base-color-scale-green-3)',
  },
  {
    background: 'var(--base-color-scale-pink-3)',
    lightName: 'var(--base-color-scale-pink-6)',
    darkName: 'var(--base-color-scale-pink-2)',
  },
] as const

const COLOR_TILES_FIRST = [
  BACKGROUNDS[0],
  tileGradient1,
  BACKGROUNDS[1],
  BACKGROUNDS[2],
  tileGradient2,
  tileGradient1,
  BACKGROUNDS[0],
  BACKGROUNDS[1],
]

function createGridLayout(columns: number, rows: number) {
  let repositoryIndex = 0
  const cells = Array.from({length: columns * rows}, (_, index) => {
    const colIndex = index % columns
    const rowIndex = Math.floor(index / columns)
    let grayOpacity = 0
    if ((colIndex + rowIndex) % 4 === 2) {
      grayOpacity = 11
    } else if ((colIndex + rowIndex) % 4 === 1) {
      grayOpacity = 4
    }
    return {
      id: `cell-${colIndex}-${rowIndex}`,
      colIndex,
      rowIndex,
      repositoryIndex: grayOpacity === 0 ? repositoryIndex++ : null,
      grayStyle: {
        '--color': grayOpacity === 0 ? 'transparent' : `color-mix(in srgb, #8D8D8D ${grayOpacity}%, transparent)`,
      } satisfies CSSProperties & {'--color': string},
    }
  })
  const revealCells = cells
    .filter(cell => cell.repositoryIndex !== null)
    .sort(
      (a, b) =>
        a.colIndex + (rows - 1 - a.rowIndex) - (b.colIndex + (rows - 1 - b.rowIndex)) || b.rowIndex - a.rowIndex,
    )
  const colorCells =
    columns === 6 && rows === 4
      ? COLOR_TILES_FIRST.map((_, index) => {
          const colIndex = Math.ceil(index / 2)
          return {colIndex, rowIndex: rows - 1 - (index - colIndex)}
        })
      : columns === 4 && rows === 6
        ? [
            {colIndex: 0, rowIndex: 4},
            {colIndex: 0, rowIndex: 3},
            {colIndex: 1, rowIndex: 3},
            {colIndex: 1, rowIndex: 2},
            {colIndex: 2, rowIndex: 2},
            {colIndex: 2, rowIndex: 1},
            {colIndex: 3, rowIndex: 1},
            {colIndex: 3, rowIndex: 0},
          ]
        : [
            {colIndex: 0, rowIndex: 4},
            {colIndex: 0, rowIndex: 3},
            {colIndex: 1, rowIndex: 3},
            {colIndex: 1, rowIndex: 2},
            {colIndex: 2, rowIndex: 2},
            {colIndex: 2, rowIndex: 1},
          ]
  const colorTiles = colorCells.slice(0, COLOR_TILES_FIRST.length).map(({colIndex, rowIndex}, index) => ({
    color: COLOR_TILES_FIRST[index],
    col: colIndex,
    row: rowIndex,
  }))

  return {
    cells: cells
      .map(cell => {
        const colorTile = colorTiles.find(tile => tile.col === cell.colIndex && tile.row === cell.rowIndex)
        const color = colorTile?.color ?? 'transparent'
        const colorBackground = color === tileGradient1 || color === tileGradient2 ? `url(${color})` : color
        const repositoryColors = REPOSITORY_COLORS[(cell.rowIndex * 2 + cell.colIndex) % REPOSITORY_COLORS.length]
        if (!repositoryColors) throw new RangeError('Invalid repository color index')
        return {
          ...cell,
          repositoryStyle: {
            '--repository-background': repositoryColors.background,
            '--repository-name-light': repositoryColors.lightName,
            '--repository-name-dark': repositoryColors.darkName,
          } satisfies CSSProperties & Record<`--repository-${string}`, string>,
          colorStyle: {
            '--color': colorBackground,
          } satisfies CSSProperties & {'--color': string},
          hoverStyle: {
            '--color': HOVER_BACKGROUNDS[(cell.rowIndex * 2 + cell.colIndex) % HOVER_BACKGROUNDS.length],
          } satisfies CSSProperties & {'--color': string | undefined},
        }
      })
      // Stable repository DOM order preserves focus when only the grid coordinates change.
      .sort((a, b) => (a.repositoryIndex ?? cells.length) - (b.repositoryIndex ?? cells.length)),
    colorTiles,
    revealCells,
  }
}

const GRID_LAYOUTS = {
  desktop: createGridLayout(6, 4),
  tablet: createGridLayout(4, 6),
  mobile: createGridLayout(3, 6),
}

function repositoryHref(url: string) {
  const parsed = new URL(url, 'https://github.com')
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new TypeError('Featured repository URLs must use HTTP or HTTPS')
  }
  return parsed.href
}

export function MonaFooterGrid({
  animate = false,
  showFinalState = false,
  repos,
  onLayoutChange,
}: {
  animate?: boolean
  showFinalState?: boolean
  repos?: MonaFooterProps['repos']
  onLayoutChange?: () => void
}) {
  const {colorMode} = useTheme()
  const gridRef = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = usePrefersReducedMotion()
  const layout = GRID_LAYOUTS[useMonaFooterLayout()]
  const previousLayoutRef = useRef(layout)
  const revealedRepositoriesRef = useRef(new Set<string>())
  const repositoriesReadyRef = useRef(false)

  useLayoutEffect(() => {
    if (previousLayoutRef.current !== layout) {
      previousLayoutRef.current = layout
      onLayoutChange?.()
    }
  }, [layout, onLayoutChange])

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid) return

    const revealedRepositories = revealedRepositoriesRef.current
    const panels = layout.revealCells.flatMap(cell => {
      const panel = grid.querySelector<HTMLElement>(`[data-cell="${cell.id}"] .${styles.RepositoryPanel}`)
      return panel ? [panel] : []
    })
    const context = gsap.context(() => {
      const isRevealed = (panel: HTMLElement) => {
        const index = panel.getAttribute('data-repository-index')
        return index !== null && revealedRepositories.has(index)
      }
      const revealedPanels = panels.filter(isRevealed)
      if (revealedPanels.length > 0) gsap.set(revealedPanels, {autoAlpha: 1, attr: {'data-revealed': 'true'}})
      const colorTiles = layout.colorTiles.map(({col, row}) => `[data-cell="cell-${col}-${row}"] .${styles.ColorTile}`)
      if (showFinalState || prefersReducedMotion || repositoriesReadyRef.current) {
        gsap.set(colorTiles, {'--opacity': 0})
        if (panels.length > 0) gsap.set(panels, {autoAlpha: 1})
        gsap.set(grid, {attr: {'data-repositories-ready': 'true'}})
        return
      }
      if (!animate) return

      const tl = gsap.timeline({
        delay: 0.8,
      })
      tl.to(colorTiles, {
        '--opacity': 1,
        duration: 0.6,
        stagger: {each: 0.3, from: 'start'},
        ease: 'power2.out',
      })
      tl.to(colorTiles, {
        '--opacity': 0,
        duration: 0.6,
        stagger: {each: 0.1, from: 'start'},
        ease: 'power2.out',
      })
      const hiddenPanels = panels.filter(panel => !isRevealed(panel))
      if (hiddenPanels.length > 0) {
        tl.to(
          hiddenPanels,
          {
            autoAlpha: 1,
            duration: 0.6,
            stagger: 0.12,
            ease: 'power2.out',
          },
          '-=0.8',
        )
      }
      tl.set(grid, {attr: {'data-repositories-ready': 'true'}})
    }, grid)

    return () => {
      for (const panel of panels) {
        const index = panel.getAttribute('data-repository-index')
        if (index !== null && panel.style.visibility && panel.style.visibility !== 'hidden') {
          revealedRepositories.add(index)
        }
      }
      repositoriesReadyRef.current = grid.getAttribute('data-repositories-ready') === 'true'
      context.revert()
    }
  }, [animate, showFinalState, prefersReducedMotion, repos?.items, layout])

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!grid || prefersReducedMotion || !window.matchMedia('(hover: hover)').matches) return

    const cleanups: Array<() => void> = []
    for (const cell of grid.querySelectorAll<HTMLElement>(`.${styles.Rect}`)) {
      const tile = cell.querySelector<HTMLElement>(`.${styles.HoverTile}`)
      if (!tile) continue

      const initialOpacity = tile.style.opacity
      let tween: gsap.core.Tween | undefined
      let hovered = false
      let fadingIn = false
      const fadeOut = () => {
        tween = gsap.to(tile, {opacity: 0, duration: 0.3, delay: 0.5, ease: 'power2.out'})
      }
      const enter = (event: PointerEvent) => {
        if (event.pointerType === 'touch' || grid.getAttribute('data-repositories-ready') !== 'true') return
        hovered = true
        tween?.kill()
        fadingIn = true
        tween = gsap.to(tile, {
          opacity: 1,
          duration: 0.3,
          ease: 'power2.out',
          onComplete: () => {
            fadingIn = false
            if (!hovered) fadeOut()
          },
        })
      }
      const leave = () => {
        if (!hovered) return
        hovered = false
        if (!fadingIn) fadeOut()
      }
      cell.addEventListener('pointerenter', enter)
      cell.addEventListener('pointerleave', leave)
      cleanups.push(() => {
        cell.removeEventListener('pointerenter', enter)
        cell.removeEventListener('pointerleave', leave)
        tween?.kill()
        tile.style.opacity = initialOpacity
      })
    }

    return () => {
      for (const cleanup of cleanups) cleanup()
    }
  }, [layout, prefersReducedMotion, repos?.items])

  return (
    <div className={styles.MonaFooterGrid} data-repository-color-mode={colorMode} ref={gridRef}>
      {layout.cells.map(cell => {
        const repository = cell.repositoryIndex === null ? undefined : repos?.items[cell.repositoryIndex]
        return (
          <div
            key={cell.repositoryIndex === null ? cell.id : `repository-${cell.repositoryIndex}`}
            data-cell={cell.id}
            className={styles.Rect}
            style={{gridColumn: cell.colIndex + 1, gridRow: cell.rowIndex + 1}}
          >
            <div className={styles.GrayTile} style={cell.grayStyle} />
            <div className={styles.ColorTile} style={cell.colorStyle} />
            {!repository && !prefersReducedMotion && (
              <div className={styles.HoverTile} style={cell.hoverStyle} aria-hidden="true" />
            )}
            {repository && repos && (
              <a
                className={styles.RepositoryPanel}
                data-repository-index={cell.repositoryIndex}
                style={cell.repositoryStyle}
                href={repositoryHref(repository.url)}
              >
                <div className={styles.RepositoryContent}>
                  <p className={styles.RepositoryName}>{repository.nameWithOwner.replace('/', ' / ')}</p>
                  {repository.description && <p className={styles.RepositoryDescription}>{repository.description}</p>}
                  <p className={styles.RepositoryStars}>
                    {repos.starsTemplate.replaceAll('{count}', String(repository.newStars))}
                  </p>
                </div>
              </a>
            )}
          </div>
        )
      })}
    </div>
  )
}
